import {
  Button,
  ConfirmModal,
  FilterSearchBar,
  OverflowTooltip,
  TablePaginationContext,
  UnLink,
  UserAvatar,
} from '@/components';
import { Pagination } from '@/components/TablePaginationContextProvider/Pagination';
import {
  SortableColumnDef,
  SortableTable,
} from '@/components/TableV2/SortableTable';
import { ToggleColumn } from '@/components/ToggleColumn';
import { RoutePath } from '@/core/router';
import { useToggleUserAccess } from '@/hooks/use-toggle-user-access';
import { useToggleUserStatus } from '@/hooks/use-toggle-user-status';
import { useDisassociateCompanyUser } from '@/modules/Companies/queries/disassociate-user';
import { CompanyAdmin } from '@/modules/Users/domain';
import { CreateCompanyUserModal } from '@/modules/Users/pages/UsersPage/components/CreateCompanyUserModal/CreateCompanyUserModal';
import { useGetAdminsByCompany } from '@/modules/Users/queries/use-get-admins-by-company';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type CompanyAdminsTabProps = {
  companyId: number;
};

export const CompanyAdminsTab = ({ companyId }: CompanyAdminsTabProps) => {
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    page,
    searchTerm,
    tableSort,
    handleSearchChange,
    onSort,
    onPageChange,
  } = useContext(TablePaginationContext);

  const sort = tableSort[0];

  const { data: admins, isLoading } = useGetAdminsByCompany({
    companyId,
    pageNumber: page,
    filter: searchTerm,
    sortBy: sort?.columnId,
    sortOrder: sort?.direction,
  });

  const { mutate: disassociateUserCompany } = useDisassociateCompanyUser();

  const { onChange: onStatusChange } = useToggleUserStatus();
  const { onChange: onAccessChange } = useToggleUserAccess();

  const columns = useMemo<SortableColumnDef<CompanyAdmin>[]>(
    () => [
      {
        id: 'name',
        name: t('name'),
        accessor: 'fullName',
        alignment: 'left',
        tdClassNames: 'min-w-0 max-w-[15vh]',
        renderRow: (row) => {
          return (
            <div
              className="flex items-center gap-2.5 overflow-hidden overflow-ellipsis"
              onClick={() => navigate(RoutePath.userDetail(row.id.toString()))}
            >
              <UserAvatar
                size="small"
                name={row.fullName}
                imageId={row.profileImageFileId}
              />
              <OverflowTooltip text={row.fullName} textClassName="font-black" />
            </div>
          );
        },
      },
      {
        id: 'email',
        name: t('email'),
        accessor: 'email',
      },
      {
        id: 'status',
        name: t('status'),
        accessor: 'status',
        renderRow: (row) => (
          <ToggleColumn
            onChange={() => onStatusChange(!row.status, row.id)}
            row={row}
            value={row.status}
          />
        ),
      },
      {
        id: 'access',
        name: t('access'),
        accessor: 'access',
        renderRow: (row) => (
          <ToggleColumn
            onChange={() => onAccessChange(!row.access, row.id)}
            row={row}
            value={row.access}
          />
        ),
      },
      {
        id: 'actions',
        name: '',
        renderRow: (row) => (
          <UnLink
            className="cursor-pointer"
            onClick={() => {
              setSelectedUserId(row.id);
            }}
          />
        ),
      },
    ],
    [navigate, onAccessChange, onStatusChange, t],
  );

  return (
    <>
      <div className="center flex flex-row">
        <FilterSearchBar
          total={admins?.totalRows || 0}
          title={t('administrators')}
          onSearch={handleSearchChange}
          variant={'secondary'}
          otherActions={
            <Button onClick={() => setIsUserModalOpen(true)} variant="primary">
              {t('add_administrator')}
            </Button>
          }
        />
      </div>
      <SortableTable
        columns={columns}
        rows={admins?.items ?? []}
        emptyStateMessage={t('no_administrators_available')}
        isLoading={isLoading}
        loadingRows={3}
        onSort={(sorts) =>
          onSort(
            sorts.map((sort) => ({
              columnId: sort.accessor,
              direction: sort.direction,
            })),
          )
        }
      />
      {!!admins?.totalPages && (
        <Pagination
          currentPage={page}
          onPageChange={onPageChange}
          totalPages={admins?.totalPages ?? 0}
        />
      )}
      <CreateCompanyUserModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        companyId={companyId}
      />
      <ConfirmModal
        title={t('please_confirm_action')}
        message={t('confirm_user_company_disassociate')}
        isOpen={!!selectedUserId}
        onConfirm={() => {
          if (selectedUserId) {
            disassociateUserCompany({
              companyId,
              userId: selectedUserId,
            });
          }

          setSelectedUserId(undefined);
        }}
        handleClose={() => {
          setSelectedUserId(undefined);
        }}
      />
    </>
  );
};
